/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * GlobalSearch — Ctrl+K command palette for navigating pages and
 * searching VENTA data (customers, invoices, products, etc.).
 *
 * Supports filter syntax: entity:status:search
 *   e.g. invoices:PAID, factures:payée:client
 *
 * Rendered once in ModuleShell; listens for Ctrl+K / Cmd+K globally.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Users,
  Receipt,
  Package,
  FileText,
  ShoppingCart,
  FileBox,
  Target,
  Filter,
  Loader2,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  X,
} from 'lucide-react';

import { navSections as ventaNavSections } from '@/modules/venta/components/VentaSidebar';
import {
  useGlobalSearch,
  type SearchCategory,
  type SearchResultItem,
  type FilterSuggestion,
} from '@/hooks/useGlobalSearch';

import type { LucideIcon } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Category config                                                    */
/* ------------------------------------------------------------------ */

interface CategoryConfig {
  icon: LucideIcon;
  labelKey: string;
}

const CATEGORY_CONFIG: Record<SearchCategory, CategoryConfig> = {
  pages:          { icon: FileText,     labelKey: 'globalSearch.pages' },
  partners:       { icon: Users,        labelKey: 'globalSearch.partners' },
  invoices:       { icon: Receipt,      labelKey: 'globalSearch.invoices' },
  products:       { icon: Package,      labelKey: 'globalSearch.products' },
  quotations:     { icon: FileText,     labelKey: 'globalSearch.quotations' },
  salesOrders:    { icon: ShoppingCart,  labelKey: 'globalSearch.salesOrders' },
  purchaseOrders: { icon: FileBox,      labelKey: 'globalSearch.purchaseOrders' },
  deals:          { icon: Target,       labelKey: 'globalSearch.deals' },
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface GlobalSearchProps {
  /** Admin-module nav sections (passed when active module is SuperAdmin) */
  adminNavSections?: { id: string; titleKey: string; items: { labelKey: string; href: string; icon: LucideIcon }[] }[];
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function GlobalSearch({ adminNavSections }: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);
  const activeIndexRef = useRef(0);

  // Merge nav sections — ventaNavSections always included, admin appended if present
  const allNavSections = adminNavSections
    ? [...ventaNavSections, ...adminNavSections]
    : ventaNavSections;

  const {
    query,
    setQuery,
    results,
    isLoading,
    isVenta,
    filterSuggestions,
    activeFilter,
  } = useGlobalSearch({ navSections: allNavSections });

  // ── Ctrl+K listener ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      activeIndexRef.current = 0;
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [open, setQuery]);

  // ── Group results by category ──
  const grouped = results.reduce<Record<string, SearchResultItem[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const categoryOrder: SearchCategory[] = [
    'pages', 'deals', 'partners', 'invoices', 'products',
    'quotations', 'salesOrders', 'purchaseOrders',
  ];
  const orderedCategories = categoryOrder.filter((c) => grouped[c]?.length);

  // Total flat count for keyboard navigation (suggestions + results)
  const totalItems = filterSuggestions.length + results.length;

  // Simple force-update mechanism for ref-based active index
  const [tick, setTick] = useState(0);
  const forceUpdate = useCallback(() => setTick((n) => n + 1), []);

  // ── Navigate to result ──
  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      setOpen(false);
      navigate(item.href);
    },
    [navigate, setOpen],
  );

  // ── Select a filter suggestion ──
  const handleSuggestionSelect = useCallback(
    (suggestion: FilterSuggestion) => {
      setQuery(suggestion.fillQuery);
      activeIndexRef.current = 0;
      forceUpdate();
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [setQuery, forceUpdate],
  );

  // ── Keyboard navigation ──
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndexRef.current = Math.min(activeIndexRef.current + 1, totalItems - 1);
        forceUpdate();
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndexRef.current = Math.max(activeIndexRef.current - 1, 0);
        forceUpdate();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const idx = activeIndexRef.current;
        if (idx < filterSuggestions.length) {
          // It's a suggestion — fill query instead of navigating
          handleSuggestionSelect(filterSuggestions[idx]);
        } else {
          const resultIdx = idx - filterSuggestions.length;
          if (results[resultIdx]) {
            handleSelect(results[resultIdx]);
          }
        }
        return;
      }
    },
    [totalItems, results, filterSuggestions, handleSelect, handleSuggestionSelect, setOpen, forceUpdate],
  );

  // Scroll active item into view
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [tick]);

  // Reset active index when results or suggestions change
  useEffect(() => {
    activeIndexRef.current = 0;
  }, [results.length, filterSuggestions.length]);

  if (!open) return null;

  const placeholder = isVenta
    ? t('globalSearch.placeholderVenta')
    : t('globalSearch.placeholderAdmin');

  // ── Render ──
  let flatIndex = 0;

  return (
    <>
      {/* Overlay */}
      <div className="gs-overlay" onClick={() => setOpen(false)} />

      {/* Modal */}
      <div className="gs-modal" onKeyDown={handleKeyDown}>
        {/* Search input */}
        <div className="gs-input-wrapper">
          <Search size={18} className="gs-input-icon" />
          <input
            ref={inputRef}
            className="gs-input"
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {isLoading && <Loader2 size={16} className="gs-spinner" />}
          <button
            className="gs-close-btn"
            onClick={() => setOpen(false)}
            aria-label={t('globalSearch.close')}
          >
            <X size={14} />
          </button>
        </div>

        {/* Active filter tags */}
        {activeFilter && (
          <div className="gs-filter-bar">
            <span className="gs-filter-tag gs-filter-entity">{activeFilter.entityLabel}</span>
            {activeFilter.statusLabel && (
              <span className="gs-filter-tag gs-filter-status">{activeFilter.statusLabel}</span>
            )}
          </div>
        )}

        {/* Results */}
        <div className="gs-results" ref={listRef}>
          {/* Filter suggestions */}
          {filterSuggestions.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-header">
                <Filter size={14} className="gs-group-icon" />
                <span>{t('globalSearch.selectStatus')}</span>
              </div>
              {filterSuggestions.map((sug) => {
                const idx = flatIndex++;
                const isActive = idx === activeIndexRef.current;
                return (
                  <div
                    key={sug.id}
                    ref={isActive ? activeItemRef : undefined}
                    className={`gs-item gs-suggestion-item ${isActive ? 'gs-item-active' : ''}`}
                    onClick={() => handleSuggestionSelect(sug)}
                    onMouseEnter={() => {
                      activeIndexRef.current = idx;
                      forceUpdate();
                    }}
                  >
                    <div className="gs-suggestion-chip">
                      <span className="gs-suggestion-label">{sug.label}</span>
                      <span className="gs-suggestion-value">{sug.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {query.trim().length > 0 && orderedCategories.length === 0 && filterSuggestions.length === 0 && !isLoading && (
            <div className="gs-no-results">{t('globalSearch.noResults')}</div>
          )}

          {query.trim().length === 0 && (
            <div className="gs-hint">
              {t('globalSearch.hint')}
              {isVenta && (
                <div className="gs-filter-tip">{t('globalSearch.filterTip')}</div>
              )}
            </div>
          )}

          {/* Data & page results */}
          {orderedCategories.map((cat) => {
            const config = CATEGORY_CONFIG[cat];
            const CatIcon = config.icon;
            return (
              <div key={cat} className="gs-group">
                <div className="gs-group-header">
                  <CatIcon size={14} className="gs-group-icon" />
                  <span>{t(config.labelKey)}</span>
                </div>
                {grouped[cat].map((item) => {
                  const idx = flatIndex++;
                  const isActive = idx === activeIndexRef.current;
                  return (
                    <div
                      key={item.id}
                      ref={isActive ? activeItemRef : undefined}
                      className={`gs-item ${isActive ? 'gs-item-active' : ''}`}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => {
                        activeIndexRef.current = idx;
                        forceUpdate();
                      }}
                    >
                      <div className="gs-item-icon-wrapper">
                        {item.icon ? (
                          <item.icon size={16} className="gs-item-icon" />
                        ) : (
                          <div className="gs-item-dot" />
                        )}
                      </div>
                      <div className="gs-item-content">
                        <div className="gs-item-title">{item.title}</div>
                        <div className="gs-item-subtitle">{item.subtitle}</div>
                      </div>
                      {item.meta && (
                        <span className={`gs-item-meta gs-meta-${item.metaVariant ?? 'default'}`}>
                          {item.meta}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer hints */}
        <div className="gs-footer">
          <div className="gs-footer-hint">
            <span className="gs-kbd"><ArrowUp size={10} /></span>
            <span className="gs-kbd"><ArrowDown size={10} /></span>
            <span className="gs-footer-label">{t('globalSearch.navigate')}</span>
          </div>
          <div className="gs-footer-hint">
            <span className="gs-kbd"><CornerDownLeft size={10} /></span>
            <span className="gs-footer-label">{t('globalSearch.open')}</span>
          </div>
          <div className="gs-footer-hint">
            <span className="gs-kbd">Esc</span>
            <span className="gs-footer-label">{t('globalSearch.close')}</span>
          </div>
        </div>
      </div>

      <style>{`
        /* ── Overlay ── */
        .gs-overlay {
          position: fixed;
          inset: 0;
          z-index: 9998;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          animation: gs-fade-in 0.15s ease;
        }

        /* ── Modal ── */
        .gs-modal {
          position: fixed;
          top: 12%;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
          width: min(560px, calc(100vw - 2rem));
          max-height: 70vh;
          display: flex;
          flex-direction: column;
          background: var(--color-bg-primary);
          border: 1px solid var(--color-border-subtle);
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          animation: gs-slide-in 0.2s ease;
        }

        /* ── Input ── */
        .gs-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--color-border-subtle);
        }

        .gs-input-icon {
          flex-shrink: 0;
          color: var(--color-text-muted);
        }

        .gs-input {
          flex: 1;
          background: none;
          border: none;
          outline: none;
          font-size: 0.9375rem;
          color: var(--color-text-primary);
          font-family: inherit;
        }

        .gs-input::placeholder {
          color: var(--color-text-muted);
        }

        .gs-spinner {
          flex-shrink: 0;
          color: var(--color-text-muted);
          animation: gs-spin 0.8s linear infinite;
        }

        .gs-close-btn {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid var(--color-border-subtle);
          background: var(--color-bg-secondary);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.15s;
        }

        .gs-close-btn:hover {
          color: var(--color-text-primary);
          background: var(--color-bg-tertiary);
        }

        /* ── Filter bar (active filter tags) ── */
        .gs-filter-bar {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 1rem;
          border-bottom: 1px solid var(--color-border-subtle);
          background: var(--color-bg-secondary);
        }

        .gs-filter-tag {
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          letter-spacing: 0.02em;
        }

        .gs-filter-entity {
          background: var(--color-bg-tertiary);
          color: var(--color-text-secondary);
        }

        .gs-filter-status {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        /* ── Results ── */
        .gs-results {
          flex: 1;
          overflow-y: auto;
          padding: 0.5rem;
          min-height: 60px;
          max-height: calc(70vh - 110px);
        }

        .gs-no-results,
        .gs-hint {
          text-align: center;
          padding: 1.5rem 1rem;
          color: var(--color-text-muted);
          font-size: 0.8125rem;
        }

        .gs-filter-tip {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          opacity: 0.7;
        }

        /* ── Group ── */
        .gs-group {
          margin-bottom: 0.25rem;
        }

        .gs-group-header {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.5rem;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--color-text-muted);
        }

        .gs-group-icon {
          opacity: 0.6;
        }

        /* ── Item ── */
        .gs-item {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.5rem 0.625rem;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.1s;
        }

        .gs-item:hover,
        .gs-item-active {
          background: var(--color-bg-hover);
        }

        .gs-item-active {
          background: var(--color-bg-hover);
          outline: 1px solid var(--color-border-subtle);
        }

        .gs-item-icon-wrapper {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          background: var(--color-bg-secondary);
        }

        .gs-item-icon {
          color: var(--color-text-secondary);
        }

        .gs-item-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--color-text-muted);
        }

        .gs-item-content {
          flex: 1;
          min-width: 0;
        }

        .gs-item-title {
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--color-text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gs-item-subtitle {
          font-size: 0.6875rem;
          color: var(--color-text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ── Suggestion items ── */
        .gs-suggestion-item {
          padding: 0.375rem 0.625rem;
        }

        .gs-suggestion-chip {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .gs-suggestion-label {
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--color-text-primary);
        }

        .gs-suggestion-value {
          font-size: 0.625rem;
          font-weight: 500;
          color: var(--color-text-muted);
          padding: 0.0625rem 0.375rem;
          border-radius: 4px;
          background: var(--color-bg-tertiary);
          font-family: monospace;
          letter-spacing: 0.02em;
        }

        /* ── Meta badge ── */
        .gs-item-meta {
          flex-shrink: 0;
          font-size: 0.625rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          padding: 0.125rem 0.375rem;
          border-radius: 9999px;
        }

        .gs-meta-default {
          background: var(--color-bg-tertiary);
          color: var(--color-text-muted);
        }

        .gs-meta-success {
          background: rgba(34, 197, 94, 0.12);
          color: #22c55e;
        }

        .gs-meta-warning {
          background: rgba(234, 179, 8, 0.12);
          color: #eab308;
        }

        .gs-meta-danger {
          background: rgba(239, 68, 68, 0.12);
          color: #ef4444;
        }

        /* ── Footer ── */
        .gs-footer {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem 1rem;
          border-top: 1px solid var(--color-border-subtle);
          background: var(--color-bg-secondary);
        }

        .gs-footer-hint {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .gs-footer-label {
          font-size: 0.6875rem;
          color: var(--color-text-muted);
        }

        .gs-kbd {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 4px;
          border-radius: 4px;
          border: 1px solid var(--color-border-subtle);
          background: var(--color-bg-primary);
          color: var(--color-text-muted);
          font-size: 0.625rem;
          font-family: inherit;
          font-weight: 500;
        }

        /* ── Animations ── */
        @keyframes gs-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes gs-slide-in {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        @keyframes gs-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default GlobalSearch;
